/**
 * useDeepgramSTT — High-performance speech-to-text hook using Deepgram via the backend.
 *
 * Flow:
 * 1. User clicks mic → MediaRecorder starts capturing audio from the browser.
 * 2. User clicks mic again (or auto-stop after max duration) → recording stops.
 * 3. Audio blob is sent to POST /transcription/transcribe on the backend.
 * 4. Backend sends audio to Deepgram Nova-2 and returns the transcript.
 * 5. Transcript is returned to the caller for injection into the chat input.
 *
 * Falls back to browser-native Web Speech API if Deepgram is not configured.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { transcriptionApi } from "@/api/services";
import type { TranscriptionResult } from "@/api/types";

// Default max recording duration (seconds) — overridden by server platform settings
const DEFAULT_MAX_DURATION_SECONDS = 120;

// Preferred MIME types in order of priority (browser support varies)
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
  "audio/mp4",
];

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const type of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

export interface UseDeepgramSTTReturn {
  /** Whether Deepgram STT is available on the server */
  available: boolean;
  /** Whether the browser supports MediaRecorder for audio capture */
  browserSupported: boolean;
  /** Whether the mic is currently recording */
  recording: boolean;
  /** Whether a transcription request is in flight */
  transcribing: boolean;
  /** The latest transcript (updated after each recording completes) */
  transcript: string;
  /** Current interim text shown during recording (visual feedback) */
  interimText: string;
  /** Last error message */
  error: string | null;
  /** Confidence score of the last transcription (0-1) */
  confidence: number;
  /** Duration of the last audio clip in seconds */
  duration: number;
  /** Start recording */
  start: () => void;
  /** Stop recording (triggers transcription) */
  stop: () => void;
  /** Clear transcript and error state */
  clear: () => void;
}

export function useDeepgramSTT(
  maxDurationSeconds: number = DEFAULT_MAX_DURATION_SECONDS,
): UseDeepgramSTTReturn {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confidence, setConfidence] = useState(0);
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Browser MIME type support doesn't change during session — compute once
  const browserSupported = useMemo(
    () =>
      typeof MediaRecorder !== "undefined" &&
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      !!getSupportedMimeType(),
    [],
  );

  // Check if Deepgram is available on the server. James's session showed
  // zero STT — the status call can transiently 401 right after login
  // before the auth cookie settles, and with only 1 retry the hook locks
  // into "available=false" for 5 minutes. Bump retry + shorten stale
  // time so a fresh login recovers fast.
  const { data: status } = useQuery({
    queryKey: ["transcription-status"],
    queryFn: () => transcriptionApi.status(),
    staleTime: 60 * 1000, // 1 minute — short enough to recover from init flakiness
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });

  const available = status?.available ?? false;
  // Server-configured max duration (falls back to default)
  const serverMaxDuration = status?.max_duration_seconds ?? maxDurationSeconds;

  /** Clear all timers safely */
  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /** Stop all media tracks */
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      stopStream();
      clearTimers();
      // Abort any in-flight transcription request
      if (abortRef.current) abortRef.current.abort();
    };
  }, [clearTimers, stopStream]);

  const processRecording = useCallback(async (blob: Blob) => {
    if (blob.size === 0) {
      setError("No audio captured. Please check your microphone.");
      return;
    }

    // Abort any previous in-flight transcription
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setTranscribing(true);
    setError(null);

    try {
      const result: TranscriptionResult = await transcriptionApi.transcribe(blob);
      // Only update if this request wasn't aborted
      if (!controller.signal.aborted) {
        if (result.transcript) {
          setTranscript(result.transcript);
          setConfidence(result.confidence);
          setDuration(result.duration);
        } else {
          setError("No speech detected. Try speaking louder or closer to the microphone.");
        }
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) return; // Intentional cancel
      const message =
        err instanceof Error ? err.message : "Transcription failed. Please try again.";
      setError(message);
    } finally {
      if (!controller.signal.aborted) {
        setTranscribing(false);
        setInterimText("");
      }
    }
  }, []);

  const stop = useCallback(() => {
    clearTimers();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    stopStream();
    setRecording(false);
  }, [clearTimers, stopStream]);

  const start = useCallback(async () => {
    if (!browserSupported) {
      setError("Your browser does not support audio recording.");
      return;
    }

    // Guard against double-start — prevent mic resource leak
    if (recording) return;

    // Reset state
    setError(null);
    setTranscript("");
    setInterimText("");
    setConfidence(0);
    setDuration(0);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 64000, // Low bitrate for voice (small files, fast upload)
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        processRecording(blob);
      };

      recorder.onerror = () => {
        setError("Recording failed. Please check your microphone permissions.");
        setRecording(false);
        clearTimers();
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current = recorder;
      recorder.start(1000); // Collect data every 1 second
      startTimeRef.current = Date.now();
      setRecording(true);

      // Visual feedback: update interim text with elapsed time
      intervalRef.current = setInterval(() => {
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
        setInterimText(`Recording... ${elapsed}s`);
      }, 1000);

      // Auto-stop after max duration
      timerRef.current = setTimeout(() => {
        stop();
      }, serverMaxDuration * 1000);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setError(
          "Microphone access denied. Please allow microphone permission in your browser settings.",
        );
      } else if (err instanceof DOMException && err.name === "NotFoundError") {
        setError("No microphone found. Please check your audio input device.");
      } else {
        setError("Failed to start recording. Please check your microphone.");
      }
      setRecording(false);
    }
  }, [browserSupported, recording, serverMaxDuration, processRecording, stop, clearTimers]);

  const clear = useCallback(() => {
    setTranscript("");
    setInterimText("");
    setError(null);
    setConfidence(0);
    setDuration(0);
  }, []);

  return {
    available,
    browserSupported,
    recording,
    transcribing,
    transcript,
    interimText,
    error,
    confidence,
    duration,
    start,
    stop,
    clear,
  };
}
